<template>
  <section class="lead-list">
    <van-dropdown-menu v-if="tagGroups.length" class="tag-filter-menu" active-color="#1677c9">
      <van-dropdown-item
        v-for="group in tagGroups"
        :key="String(group.id)"
        :ref="'tagDropdown-' + String(group.id)"
        :title="tagFilterTitle(group)"
      >
        <div class="tag-filter-panel">
          <van-checkbox-group v-model="tagSelections[String(group.id)]">
            <van-cell-group>
              <van-cell
                v-for="tag in group.items"
                :key="String(tag.value)"
                clickable
                :title="tag.label"
                @click="toggleTag(group, tag)"
              >
                <template #right-icon>
                  <van-checkbox :ref="tagCheckboxRef(group, tag)" :name="tag.value" :disabled="tag.disabled" @click.stop />
                </template>
              </van-cell>
            </van-cell-group>
          </van-checkbox-group>
          <div class="tag-filter-actions">
            <van-button size="small" plain @click="clearTagGroup(group)">清空</van-button>
            <van-button size="small" type="info" @click="applyTagGroup(group)">确定</van-button>
          </div>
        </div>
      </van-dropdown-item>
    </van-dropdown-menu>

    <div class="lead-count">共 {{ total }} 条客资</div>
    <div v-if="error" class="lead-error">
      <span>客资列表加载失败</span>
      <van-button size="small" type="info" plain @click="resetList">重新加载</van-button>
    </div>
    <van-list v-else v-model="loading" :finished="finished" :finished-text="rows.length ? '没有更多了' : ''" @load="loadMore">
      <div
        v-for="lead in rows"
        :key="lead.leadId || lead.id"
        class="lead-row"
        role="button"
        tabindex="0"
        @click="openDetail(lead)"
        @keydown.enter.prevent="openDetail(lead)"
      >
        <span class="lead-avatar">
          <img v-if="lead.avatar && !lead.avatarFailed" :src="lead.avatar" alt="" @error="$set(lead, 'avatarFailed', true)" />
          <span v-else>{{ initial(lead.name) }}</span>
        </span>
        <span class="lead-main">
          <span class="lead-heading">
            <strong>{{ leadDisplayName(lead.name) }}</strong>
            <span :class="['wecom-state', isLeadBound(lead) ? 'linked' : 'unlinked']">{{ bindingStatusText(lead) }}</span>
          </span>
          <span class="lead-sub">
            <span class="mobile-text">{{ displayValue(lead.mobile) }}</span>
            <button type="button" class="call-button" :aria-label="'拨打 ' + leadDisplayName(lead.name) + ' 的电话'" @click.stop="callLead(lead)">
              <van-loading v-if="callingLeadId === Number(lead.leadId || lead.id)" size="14px" />
              <van-icon v-else name="phone-o" />
            </button>
          </span>
          <span class="lead-meta">负责人：{{ displayValue(lead.ownerEmployeeName) }}</span>
          <span class="lead-follow">最近跟进 {{ displayValue(lead.lastFollowAt) }}</span>
        </span>
        <van-icon class="lead-arrow" name="arrow" />
      </div>
    </van-list>
    <van-empty v-if="!loading && !error && finished && !rows.length" description="暂无客资" />
  </section>
</template>

<script>
import { getLeadList, getLeadOptions, revealLeadIdentity } from '../../api/crmLead'
import { buildWecomTagFilters, displayValue, isLeadBound, leadDisplayName, normalizeWecomTagGroups } from './leadView'
import { captureListScroll, clearLeadListState, leadListScope, restoreListScroll, saveLeadListState, takeLeadListState } from './listState'

export default {
  name: 'LeadList',
  data () {
    return {
      keyword: '',
      listScope: '',
      initializing: true,
      optionsLoaded: false,
      appliedKeyword: '',
      options: {},
      tagSelections: {},
      rows: [],
      page: 1,
      total: 0,
      loading: false,
      fetching: false,
      finished: false,
      error: false,
      requestSequence: 0,
      callingLeadId: 0
    }
  },
  computed: {
    tagGroups () {
      return normalizeWecomTagGroups(this.options.wecomTagGroups)
    }
  },
  created () {
    this.initializeList()
  },
  methods: {
    displayValue,
    isLeadBound,
    leadDisplayName,
    async initializeList () {
      this.initializing = true
      this.listScope = leadListScope(this.$route)
      const saved = takeLeadListState(this.listScope)
      if (saved) {
        this.keyword = saved.keyword
        this.appliedKeyword = saved.appliedKeyword
        this.tagSelections = saved.tagSelections
        this.$emit('restore-search', this.keyword)
      }
      try {
        const response = await getLeadOptions()
        this.options = response.data || {}
        this.initializeTagSelections()
        this.optionsLoaded = true
      } catch (error) {
        this.error = true
        this.initializing = false
        this.$toast.fail('客资筛选项加载失败，请重新加载')
        return
      }
      const pages = saved ? saved.loadedPages : 1
      for (let page = 0; page < pages && !this.finished && !this.error; page++) await this.loadMore(true)
      this.initializing = false
      if (saved) this.$nextTick(() => restoreListScroll(this.$el, saved.scroll))
    },
    saveReturnState () {
      saveLeadListState(this.listScope, {
        keyword: this.keyword,
        appliedKeyword: this.appliedKeyword,
        tagSelections: this.tagSelections,
        loadedPages: Math.max(1, this.page - 1),
        scroll: captureListScroll(this.$el)
      })
    },
    bindingStatusText (lead) {
      return ({ unbound: '未关联企微', pending: '等待扫码', awaiting_owner: '已关联，关系待确认', bound: '已关联企微', conflict: '绑定冲突', expired: '二维码过期' })[lead.bindingStatus] || (this.isLeadBound(lead) ? '已关联企微' : '未关联企微')
    },
    initializeTagSelections () {
      this.tagGroups.forEach(group => {
        const groupId = String(group.id)
        if (!Array.isArray(this.tagSelections[groupId])) this.$set(this.tagSelections, groupId, [])
      })
    },
    tagFilterTitle (group) {
      const count = (this.tagSelections[String(group.id)] || []).length
      return count ? `${group.name} (${count})` : group.name
    },
    tagCheckboxRef (group, tag) {
      return `tagCheckbox-${String(group.id)}-${String(tag.value)}`
    },
    toggleTag (group, tag) {
      if (tag.disabled) return
      const checkbox = this.$refs[this.tagCheckboxRef(group, tag)]
      const instance = Array.isArray(checkbox) ? checkbox[0] : checkbox
      if (instance) instance.toggle()
    },
    clearTagGroup (group) {
      this.$set(this.tagSelections, String(group.id), [])
      this.closeTagDropdown(group)
      this.resetList()
    },
    applyTagGroup (group) {
      this.closeTagDropdown(group)
      this.resetList()
    },
    closeTagDropdown (group) {
      const dropdown = this.$refs[`tagDropdown-${String(group.id)}`]
      const instance = Array.isArray(dropdown) ? dropdown[0] : dropdown
      if (instance && typeof instance.toggle === 'function') instance.toggle(false)
    },
    initial (name) { return String(name || '客').trim().slice(0, 1) || '客' },
    openDetail (lead) {
      this.$router.push({ path: '/crmLead/detail', query: { leadId: lead.leadId || lead.id } })
    },
    async callLead (lead) {
      const leadId = Number(lead.leadId || lead.id || 0)
      if (!leadId || this.callingLeadId) return
      this.callingLeadId = leadId
      try {
        const response = await revealLeadIdentity({ leadId, type: 'mobile' })
        const mobile = this.normalizeDialNumber((response.data || {}).value)
        if (!mobile) {
          this.$toast.fail('未获取到可拨打的手机号')
          return
        }
        if (typeof window !== 'undefined') window.location.href = `tel:${mobile}`
      } catch (error) {
        const responseData = error && error.response && error.response.data
        this.$toast.fail((responseData && (responseData.msg || responseData.message)) || '手机号读取失败，请稍后重试')
      } finally {
        this.callingLeadId = 0
      }
    },
    normalizeDialNumber (value) {
      const raw = String(value || '').trim()
      const hasLeadingPlus = raw.startsWith('+')
      const digits = raw.replace(/\D/g, '')
      return digits.length >= 6 && digits.length <= 20 ? `${hasLeadingPlus ? '+' : ''}${digits}` : ''
    },
    applySearch (keyword) {
      const nextKeyword = String(keyword || '')
      if (nextKeyword === this.keyword && nextKeyword.trim() === this.appliedKeyword) return
      this.keyword = nextKeyword
      this.resetList()
    },
    resetList () {
      clearLeadListState()
      this.requestSequence += 1
      this.appliedKeyword = this.keyword.trim()
      this.rows = []
      this.page = 1
      this.total = 0
      this.finished = false
      this.error = false
      this.fetching = false
      this.loading = false
      if (this.optionsLoaded) this.$nextTick(this.loadMore)
      else this.initializeList()
    },
    async loadMore (restoring = false) {
      if ((this.initializing && !restoring) || this.fetching || this.finished || this.error) return
      this.fetching = true
      this.loading = true
      const sequence = this.requestSequence
      const wecomTagFilters = buildWecomTagFilters(this.tagGroups, this.tagSelections)
      try {
        const response = await getLeadList({
          page: this.page,
          perPage: 20,
          keyword: this.appliedKeyword,
          wecomTagFilters: wecomTagFilters.length ? JSON.stringify(wecomTagFilters) : undefined
        })
        if (sequence !== this.requestSequence) return
        const data = response.data || {}
        const nextRows = Array.isArray(data.list) ? data.list : []
        this.rows.push(...nextRows)
        this.total = Number((data.page || {}).total || 0)
        this.page += 1
        this.finished = nextRows.length < 20 || this.rows.length >= this.total
      } catch (error) {
        if (sequence === this.requestSequence) this.error = true
      } finally {
        if (sequence === this.requestSequence) {
          this.fetching = false
          this.loading = false
        }
      }
    }
  }
}
</script>

<style lang="less" scoped>
.lead-list { min-height: calc(100vh - 190px); padding-bottom: 70px; background: #f1f2f5; }
.tag-filter-menu { position: sticky; top: 0; z-index: 2; }
.tag-filter-menu /deep/ .van-dropdown-menu__bar { overflow-x: auto; justify-content: flex-start; box-shadow: 0 1px 4px rgba(0, 0, 0, .06); }
.tag-filter-menu /deep/ .van-dropdown-menu__item { flex: 0 0 auto; min-width: 110px; padding: 0 10px; }
.tag-filter-menu /deep/ .van-dropdown-menu__title { max-width: 132px; }
.tag-filter-panel { max-height: 62vh; overflow-y: auto; background: #fff; }
.tag-filter-actions { position: sticky; bottom: 0; display: flex; justify-content: flex-end; gap: 10px; padding: 10px 16px; border-top: 1px solid #edf0f2; background: #fff; }
.lead-count { padding: 14px 16px 8px; color: #666; font-size: 13px; }
.lead-row { display: flex; align-items: flex-start; gap: 12px; width: 100%; padding: 14px 16px; border-bottom: 1px solid #edf0f2; background: #fff; color: #222; text-align: left; box-sizing: border-box; }
.lead-row:focus-visible { outline: 2px solid #1677c9; outline-offset: -2px; }
.lead-avatar { display: grid; place-items: center; flex: 0 0 42px; width: 42px; height: 42px; border-radius: 4px; background: #dfece9; color: #237462; font-size: 18px; font-weight: 600; }
.lead-avatar img { display: block; width: 42px; height: 42px; border-radius: 4px; object-fit: cover; }
.lead-main { display: flex; flex: 1; min-width: 0; flex-direction: column; gap: 5px; }
.lead-heading { display: flex; align-items: center; gap: 8px; min-width: 0; }
.lead-heading strong { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; font-size: 15px; font-weight: 600; }
.wecom-state { flex: none; padding: 2px 5px; border-radius: 3px; font-size: 11px; }
.wecom-state.linked { background: #e8f5ed; color: #237747; }
.wecom-state.unlinked { background: #f2f4f6; color: #69747b; }
.lead-sub { display: flex; align-items: center; min-width: 0; color: #65717a; font-size: 12px; }
.mobile-text { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.call-button { display: inline-grid; place-items: center; flex: 0 0 28px; width: 28px; height: 28px; margin: -6px 0 -6px 4px; padding: 0; border: 0; border-radius: 50%; background: #eef6fc; color: #1677c9; font-size: 17px; }
.lead-meta, .lead-follow { overflow: hidden; color: #65717a; font-size: 12px; text-overflow: ellipsis; white-space: nowrap; }
.lead-follow { color: #8a939b; }
.lead-arrow { align-self: center; color: #b8c0c6; }
.lead-error { display: flex; align-items: center; justify-content: space-between; margin: 10px 16px; padding: 14px; background: #fff; color: #a0443f; }
</style>
